import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { PermissionGuard } from '../lib/auth/helpers';
import { ArrowLeftIcon, ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { msalInstance } from '../contexts/AuthContext';
import { getApiScope } from '../config';

// Declare Power BI types
declare global {
  interface Window {
    powerbi: any;
  }
}

export const PowerBIPage: React.FC = () => {
  const navigate = useNavigate();
  const [embedConfig, setEmbedConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const embedContainerRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<any>(null);

  // Load Power BI SDK
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client@2.22.3/dist/powerbi.min.js';
    script.async = true;
    script.onload = () => {
      console.log('Power BI SDK loaded');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Embed the report when config is available
  useEffect(() => {
    if (embedConfig && embedContainerRef.current) {
      // Wait for Power BI SDK to load
      const embedReport = () => {
        if (!window.powerbi) {
          console.log('Waiting for Power BI SDK...');
          setTimeout(embedReport, 500);
          return;
        }
        
        console.log('Embedding Power BI report...');
        console.log('Embed config:', embedConfig);
        
        const config = {
          type: 'report',
          id: embedConfig.reportId,
          embedUrl: embedConfig.embedUrl,
          accessToken: embedConfig.embedToken,
          tokenType: 1, // 1 = Embed token
          settings: {
            filterPaneEnabled: false,
            navContentPaneEnabled: false
          }
        };

        try {
          // Embed the report
          reportRef.current = window.powerbi.embed(embedContainerRef.current, config);

          // Handle loaded event
          reportRef.current.on('loaded', () => {
            console.log('Report loaded successfully');
          });

          // Handle rendered event
          reportRef.current.on('rendered', () => {
            console.log('Report rendered successfully');
          });

          // Handle error event
          reportRef.current.on('error', (event: any) => {
            const error = event.detail;
            console.error('Power BI Error:', error);
            setError(`Power BI Error: ${error.message || 'Unknown error'}`);
          });
        } catch (err) {
          console.error('Failed to embed report:', err);
          setError('Failed to embed Power BI report');
        }
      };
      
      embedReport();
    }
  }, [embedConfig]);

  useEffect(() => {
    const fetchEmbedToken = async () => {
      try {
        console.log('Fetching Power BI embed token...');
        
        // Check if authentication is enabled
        const authEnabled = process.env.REACT_APP_ENABLE_AUTHENTICATION === 'true';
        
        let headers: any = {
          'Content-Type': 'application/json'
        };

        // Get token if authentication is enabled
        if (authEnabled) {
          try {
            // Get accounts from MSAL
            const accounts = msalInstance.getAllAccounts();
            console.log('MSAL accounts:', accounts.length);
            
            if (accounts.length > 0) {
              // Acquire token silently
              const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: [getApiScope()], // Use scope from .env
                account: accounts[0]
              });
              
              console.log('Token acquired successfully');
              headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            } else {
              // No accounts available, wait a bit and retry
              console.log('No accounts available yet, waiting...');
              setTimeout(() => {
                fetchEmbedToken();
              }, 1000);
              return;
            }
          } catch (tokenError: any) {
            console.error('Failed to acquire token:', tokenError);
            
            // If interaction is required, trigger login
            if (tokenError.name === 'InteractionRequiredAuthError') {
              console.log('Interaction required, redirecting to login...');
              await msalInstance.loginRedirect({
                scopes: [getApiScope()] // Use scope from .env
              });
              return;
            }
            
            setError('Authentication failed. Please refresh the page.');
            setLoading(false);
            return;
          }
        }
        
        // Make the API call
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/powerbi/embed-token`;
        console.log('Calling API:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch Power BI embed token (${response.status})`);
        }
        
        const result = await response.json();
        console.log('API Response:', result);
        
        // Check for both possible response formats
        if ((result.status === 'success' || result.success === true) && result.data) {
          setEmbedConfig(result.data);
          console.log('Embed config set:', result.data);
        } else {
          throw new Error(result.message || 'Invalid response format');
        }
        
      } catch (err: any) {
        console.error('Error in fetchEmbedToken:', err);
        setError(err.message || 'Failed to load Power BI dashboard');
      } finally {
        setLoading(false);
      }
    };

    // Start fetching immediately
    fetchEmbedToken();
  }, []); // Run once on mount

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="h-8 w-8 text-indigo-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">NEC Passenger Flow Management Analytics</h1>
              {/* <p className="mt-2 text-gray-400">
                Business intelligence and data analytics
              </p> */}
            </div>
          </div>
        </div>

        {/* PowerBI Content */}
        <PermissionGuard feature="powerBI" action="any">
          {loading && (
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <div className="animate-pulse">
                <ChartBarIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Loading Power BI dashboard...</p>
              </div>
            </div>
          )}
          
          {error && !loading && (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-red-400 font-medium">Failed to load dashboard</h3>
                  <p className="text-red-400/80 text-sm mt-1">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {embedConfig && !error && !loading && (
            <>
              <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
                <div 
                  ref={embedContainerRef}
                  className="w-full"
                  style={{ height: '80vh', minHeight: '600px' }}
                />
              </div>

              {/* Information Panel */}
            </>
          )}
        </PermissionGuard>

        {/* Access Denied Message */}
        <PermissionGuard 
          feature="powerBI" 
          action="any" 
          fallback={
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <ChartBarIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Access Restricted</h2>
              <p className="text-gray-400">You don't have permission to access PowerBI analytics.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          }
        >
          <div></div>
        </PermissionGuard>
      </main>
    </div>
  );
};
